/**
 * Atendimento / inbox (drill-down de "conversas aguardando" e "SLA").
 *
 * É a tela de inbox que antes vinha de fixture (`renderInbox`) migrada para
 * dado real: as conversas do `ConversationCore` são convertidas para o MESMO
 * contrato de UI já existente (`ConversationListItem` de `packages/ui`) e
 * validadas por `assertConversationListItem`.
 *
 * Drill-down do SLA: a lista recebe a **fila inteira** usada no cálculo
 * (denominador) e classifica cada linha em dentro/fora da janela, exibindo o
 * numerador no cabeçalho. É assim que o percentual do card fica auditável aqui
 * — antes a tela mostrava só as violações e o denominador não era navegável
 * (D-10.19).
 *
 * O canal de WhatsApp é exibido como "WAPI da TDZAIN" (ver `format.ts`); o
 * fornecedor fica contido no adapter, nunca na UI.
 *
 * Migrado para `.tsx`/JSX real (Cluster C — Atendimento & CRM, redesign por
 * perfil; ver `docs/architecture/ADR-0011-*.md`). Mudança de sintaxe
 * (`createElement` → JSX) + um ícone lucide (`MessageSquare`, o mesmo do
 * grupo de navegação "Atendimento & CRM" em `AppShell.tsx`) na nota da janela
 * de SLA, no mesmo padrão visual de `KpiCard.tsx` (ícone + texto, nunca só
 * cor) — esta tela não tinha nenhum ícone antes. Nenhum `data-*`/texto/rota
 * muda.
 */
// Import explícito de `React` — ver o mesmo comentário em `AppShell.tsx`.
import React, { type ReactElement } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MessageSquare } from "lucide-react";
import { Avatar, Badge, EmptyState, buttonClass } from "../ui/index.js";
import { DatasetBoundary } from "../components/DatasetBoundary.js";
import { DrillDownHeader } from "../components/DrillDownHeader.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { conversationsForKpi, parseKpiId } from "../dashboard/drilldown.js";
import {
  AWAITING_CONTRACT_LABEL,
  classifySlaStatus,
  isSourceAwaitingContract,
  lastCustomerMessageAt,
  slaBreakdown,
  slaWaitingMinutes,
  SLA_FIRST_RESPONSE_TARGET_MINUTES,
  toConversationListItem,
  type DashboardDataset
} from "../dashboard/kpis.js";
import { formatChannel, formatCount, formatDateTime } from "../format.js";
import type { DrillDownViewProps } from "../dashboard/view-props.js";
import type { PlatformConversation } from "../api/types.js";

export function InboxView({ dataset, kpiId, now }: DrillDownViewProps): ReactElement {
  const reference = now ?? new Date();
  // Rota de conversas ausente no ambiente (Worker de produção): a tela declara
  // a ausência da FONTE. Uma tabela vazia aqui seria lida como "ninguém
  // esperando atendimento".
  if (isSourceAwaitingContract(dataset, "conversations")) {
    return (
      <div className="page page--inbox flex flex-col gap-6" data-page="inbox">
        <DrillDownHeader title="Atendimento" kpiId={kpiId} recordCount={0} recordNoun="conversa(s)" />
        <StatusPanel
          kind="awaiting_source"
          title={`Lista de conversas ${AWAITING_CONTRACT_LABEL}`}
          detail="A rota GET /api/channels/conversations já tem paridade Worker → Edge Functions (Task 18), mas ainda não respondeu com dados neste ambiente. Enquanto a fonte não carrega, o indicador fica sem valor: exibir zero diria que não há conversa aguardando."
        />
      </div>
    );
  }
  const conversations = conversationsForKpi(dataset, kpiId, reference);
  const byId = new Map<string, PlatformConversation>(
    conversations.map((conversation) => [conversation.id, conversation])
  );
  const items = conversations.map(toConversationListItem);
  const sla = slaBreakdown(dataset, reference);
  const showingSlaPopulation = kpiId === "sla_atendimento";
  return (
    <div className="page page--inbox flex flex-col gap-6" data-page="inbox">
      <DrillDownHeader title="Atendimento" kpiId={kpiId} recordCount={items.length} recordNoun="conversa(s)" />
      <p className="page__note flex items-center gap-2 text-sm text-muted">
        <MessageSquare className="h-4 w-4 shrink-0 text-brand-text" aria-hidden focusable={false} />
        {`Janela de SLA de primeira resposta humana: ${SLA_FIRST_RESPONSE_TARGET_MINUTES} minutos.`}
      </p>
      {showingSlaPopulation ? (
        <p
          className="page__note text-sm text-muted"
          data-sla-population={String(sla.queue.length)}
          data-sla-within={String(sla.within.length)}
          data-sla-breached={String(sla.breached.length)}
        >
          {sla.compliancePct === null
            ? "Sem conversas na fila humana: não há SLA a medir neste instante."
            : `${sla.within.length} de ${sla.queue.length} conversa(s) dentro da janela = ${sla.compliancePct}% de SLA. A lista abaixo traz a fila inteira usada no cálculo, classificada linha a linha.`}
        </p>
      ) : null}
      {items.length === 0 ? (
        <EmptyState icon={MessageSquare} title="Nenhuma conversa neste recorte" description="Quando um cliente escrever no WhatsApp ou no chat da loja, a conversa aparece aqui." />
      ) : (
        <ul className="m-0 flex list-none flex-col gap-3 p-0" aria-label="Conversas">
          {items.map((item) => {
            const conversation = byId.get(item.id);
            const waitingSince = conversation ? lastCustomerMessageAt(conversation) : null;
            const slaStatus = conversation && item.status === "pending" ? classifySlaStatus(conversation, reference) : null;
            const minutes = conversation && slaStatus ? slaWaitingMinutes(conversation, reference) : null;
            const human = item.assignedTo === "human";
            return (
              <li
                key={item.id}
                data-record-id={item.id}
                className="flex flex-wrap items-center gap-4 rounded-panel border border-border bg-surface p-4 shadow-s1 transition-colors hover:border-brand"
              >
                <Avatar name={item.customerName} />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link to={`/clientes/${item.customerId}`} className="text-base font-extrabold text-ink hover:underline">
                      {item.customerName}
                    </Link>
                    <Badge tone={item.channel === "whatsapp" ? "success" : "neutral"} title={formatChannel(item.channel)}>{item.channel === "whatsapp" ? "WhatsApp" : "Chat da loja"}</Badge>
                    <Badge tone={human ? "warning" : "info"}>{human ? "Humano" : "IA"}</Badge>
                    {slaStatus ? (
                      <span data-sla-status={slaStatus}>
                        <Badge tone={slaStatus === "breached" ? "danger" : "success"}>
                          {slaStatus === "breached" ? "fora do SLA" : "dentro do SLA"}
                          {minutes === null ? "" : ` (${formatCount(minutes)} min)`}
                        </Badge>
                      </span>
                    ) : null}
                  </div>
                  <p className="m-0 truncate text-sm text-muted">{item.lastMessagePreview || "Sem mensagens."}</p>
                </div>
                <div className="flex flex-col items-end gap-1 text-xs text-muted">
                  <span>{waitingSince ? `Aguardando desde ${formatDateTime(waitingSince)}` : formatDateTime(item.lastMessageAt)}</span>
                  <span className="font-bold">{`Lead score ${formatCount(item.leadScore)}`}</span>
                </div>
                <Link to={`/inbox/${item.id}`} className={buttonClass("outline", "md")}>
                  Abrir conversa
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function Inbox(): ReactElement {
  const [searchParams] = useSearchParams();
  const kpiId = parseKpiId(searchParams.get("kpi"));
  return (
    <DatasetBoundary>{(dataset: DashboardDataset) => <InboxView dataset={dataset} kpiId={kpiId} />}</DatasetBoundary>
  );
}
